<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $t('messages.attendance_review'),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div class="flex gap-x-1 items-center">
                        <BadgePill>
                            <a :href="meta.links.graphs_index">{{ $t('messages.graphs') }}</a>
                        </BadgePill>
                        <BadgePill>
                            <InertiaLink :href="meta.links.stats_index">{{ $t('messages.stats') }}</InertiaLink>
                        </BadgePill>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <DropdownButton>
            <template v-slot:content>
                <a
                    :href="meta.links.groups_show"
                    class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                    role="menuitem"
                >
                    {{ $tc('messages.groups', 1) }}
                </a>
                <a
                    :href="meta.links.monthly_attendance_report"
                    class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                    role="menuitem"
                >
                    {{ $t('messages.monthly_attendance_report') }}
                </a>
            </template>
        </DropdownButton>

        <form method="GET" action="" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from" />
                        <FormInput id="date_from" v-model="form.date_from" type="date" name="date_from" class="mt-1" />
                        <FormError :value="form.errors.date_from" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to" />
                        <FormInput id="date_to" v-model="form.date_to" type="date" name="date_to" class="mt-1" />
                        <FormError :value="form.errors.date_to" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-8 align-bottom">
                    <label class="block text-sm font-medium leading-5 text-gray-700 mt-1"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="attendances.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else>
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.date') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.turn') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.actions') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(attendance, index) in attendances.data"
                                    :key="attendance.id"
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                >
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                        {{ attendance.date_display }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        {{ attendance.turn_display }}
                                    </td>
                                    <td
                                        class="px-6 py-4 whitespace-nowrap text-right text-sm leading-5 font-medium flex gap-x-2 justify-end"
                                    >
                                        <InertiaSquareLinkButton action="show" :href="attendance.links.show" />
                                        <InertiaSquareLinkButton
                                            action="edit"
                                            :href="attendance.links.edit"
                                            v-if="attendance.can.edit"
                                        />
                                        <InertiaSquareDeleteButton
                                            v-if="attendance.can.delete"
                                            :href="attendance.links.delete"
                                            :confirm-text="$t('messages.are_you_sure?')"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="attendances" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormError from '@/components/Forms/FormError.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import BadgePill from '@/components/BadgePill.vue';
import DropdownButton from '@/components/DropdownButton.vue';

export default {
    components: {
        DropdownButton,
        BadgePill,
        InertiaSquareDeleteButton,
        InertiaSquareLinkButton,
        InertiaPagination,
        SubmitFormButton,
        FormError,
        FormInput,
        FormLabel,
        PanelPage,
        EmptyState,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'attendances', 'group', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
};
</script>
